import React, { useMemo, useState, useEffect } from "react";
import useCityAutocomplete from "../hooks/useCityAutocomplete";
import "./CityField.css";

/**
 * CityField (riusabile)
 * - hardConfirm: se true, richiede selezione da elenco (confirmed=true)
 * - quando l’utente scrive: confirmed=false
 * - FIX: se hardConfirm && !confirmed, NON filtrare per CAP/provincia
 */
export default function CityField({
  label = "Città",
  value,
  provinciaValue,
  capValue,
  onChangeCity, // (newCityStr, meta?) => void
  onApplyCity, // ({name, provincia, cap}) => void
  onBlur,
  enabled = true,
  dropdownPlacement = "bottom", // "bottom" | "top"

  // UX
  placeholder = "Inizia a scrivere…",
  hardConfirm = false,
  confirmed = true,
  setConfirmed = () => {},

  // reset behavior
  resetProvinciaOnType = true,
  resetCapOnType = false,

  // UI errors
  error = "",
  showEmptyHint = true,

  // class hooks
  inputClassName = "",
  wrapperClassName = "",
  errorClassName = "error-text",
}) {
  const [open, setOpen] = useState(false);

  // ✅ TOUCHED: l'utente deve prima interagire col campo (entrare ed uscire)
  // prima che l'errore "hard confirm" possa comparire. Evita l'errore "eager"
  // mostrato subito all'apertura del form, quando confirmed parte a false.
  const [touched, setTouched] = useState(false);

  // ✅ se hardConfirm e NON confermato, ignora filtri provincia/CAP
  const effProvincia = hardConfirm && !confirmed ? "" : provinciaValue;
  const effCap = hardConfirm && !confirmed ? "" : capValue;

  const { suggestions, loading, err, setSuggestions } = useCityAutocomplete({
    citta: value,
    provincia: effProvincia,
    cap: effCap,
    enabled,
  });

  // ✅ QUI: stile posizionamento dropdown (solo relativo a questo campo)
  const dropStyle =
    dropdownPlacement === "top"
      ? { bottom: "calc(100% + 6px)", top: "auto" }
      : { top: "calc(100% + 6px)", bottom: "auto" };

  const hasQuery = String(value || "").trim().length >= 2;

  // se hardConfirm e già confermato, non ha senso continuare a suggerire finché non riscrivi
  const shouldSuggest = !(hardConfirm && confirmed);

  // se disabilitato, chiudi
  useEffect(() => {
    if (!enabled) setOpen(false);
  }, [enabled]);

  const showBox =
    open &&
    enabled &&
    hasQuery &&
    shouldSuggest &&
    (loading || suggestions.length > 0 || (showEmptyHint && !loading && !err));

  const emptyText = useMemo(() => {
    const q = String(value || "").trim();
    return q ? `Nessun comune trovato per “${q}”` : "Nessun risultato";
  }, [value]);

  const keyFor = (c) => `${c.name || ""}-${c.provincia || ""}-${c.cap || ""}`;

  const invalidByConfirm = hardConfirm && !confirmed && touched;

  return (
    <div className={`alc-cityfield ${wrapperClassName}`}>
      <label className="alc-cityfield-label">{label}</label>

      {/* 🔑 wrap relativo: la box si ancora SOLO a questo input */}
      <div className="alc-cityfield-wrap">
        <input
          value={value}
          onFocus={() => {
            if (enabled) setOpen(true);
          }}
          onChange={(e) => {
            const v = e.target.value;

            // quando scrivi => non confermato
            if (hardConfirm) setConfirmed(false);

            onChangeCity?.(v, {
              resetProvincia: resetProvinciaOnType,
              resetCap: resetCapOnType,
            });

            if (enabled) setOpen(true);
          }}
          onBlur={(e) => {
            setTouched(true);
            onBlur?.(e);
            // lascia tempo al click suggestion
            window.setTimeout(() => setOpen(false), 140);
          }}
          autoComplete="off"
          placeholder={placeholder}
          className={`${inputClassName}${
            error || invalidByConfirm ? " input-invalid" : ""
          }`}
        />

        {showBox && (
          <div
            className="alc-cityfield-box"
            style={dropStyle} // ✅ ora si apre sopra/sotto SOLO qui
          >
            {loading && (
              <div className="alc-cityfield-item alc-cityfield-item--disabled">
                Cerco i comuni…
              </div>
            )}

            {!loading && err && (
              <div className="alc-cityfield-item alc-cityfield-item--disabled">
                Errore: {err}
              </div>
            )}

            {!loading &&
              !err &&
              suggestions.map((c) => (
                <button
                  key={keyFor(c)}
                  type="button"
                  className="alc-cityfield-item"
                  onMouseDown={(ev) => {
                    ev.preventDefault();

                    const payload = {
                      name: c.name || "",
                      provincia: (c.provincia || "").toUpperCase(),
                      cap: c.cap || "",
                    };

                    onApplyCity?.(payload);

                    if (hardConfirm) setConfirmed(true);

                    // chiudi e pulisci suggestion: evita rimbalzi
                    setSuggestions([]);
                    setOpen(false);
                  }}
                >
                  <span className="alc-cityfield-name">{c.name}</span>
                  {c.provincia && (
                    <span className="alc-cityfield-meta"> ({c.provincia})</span>
                  )}
                  {c.cap && (
                    <span className="alc-cityfield-meta"> – {c.cap}</span>
                  )}
                </button>
              ))}

            {!loading && !err && suggestions.length === 0 && showEmptyHint && (
              <div className="alc-cityfield-item alc-cityfield-item--disabled">
                {emptyText}
              </div>
            )}
          </div>
        )}
      </div>

      {/* error precedence: form error > confirm error */}
      {error ? (
        <div className={errorClassName}>{error}</div>
      ) : invalidByConfirm ? (
        <div className={errorClassName}>Seleziona una città dall’elenco</div>
      ) : null}
    </div>
  );
}
